/**
 * Trunnion AI - DataTable Component
 * Sortable, filterable table with pagination.
 */

import { useState, useMemo, type ReactNode } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  loading?: boolean;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  loading = false,
  emptyMessage = 'No data found',
  emptyIcon,
  page = 1,
  pageSize = 25,
  total,
  onPageChange,
  onRowClick,
  sortKey,
  sortDirection = 'asc',
  onSort,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const activeSort = sortKey
    ? { key: sortKey, direction: sortDirection }
    : internalSort;

  const handleSort = (key: string) => {
    const newDirection =
      activeSort?.key === key && activeSort.direction === 'asc' ? 'desc' : 'asc';

    if (onSort) {
      onSort(key, newDirection);
    } else {
      setInternalSort({ key, direction: newDirection });
    }
  };

  const totalPages = total ? Math.ceil(total / pageSize) : 1;
  const showPagination = total !== undefined && total > pageSize;

  const sortedData = useMemo(() => {
    if (!internalSort || onSort) return data;
    return [...data].sort(() => 0); // External sort should be handled by API
  }, [data, internalSort, onSort]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (data.length === 0) {
    return <EmptyState message={emptyMessage} icon={emptyIcon} />;
  }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto rounded-lg border border-trunnion-slate-700">
        <table className="w-full text-sm" role="table">
          <thead>
            <tr className="border-b border-trunnion-slate-700 bg-trunnion-slate-800/80">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-4 py-3 text-left font-medium text-trunnion-slate-300 ${
                    col.sortable ? 'cursor-pointer select-none hover:text-white' : ''
                  }`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => col.sortable && handleSort(col.key)}
                  aria-sort={activeSort?.key === col.key ? (activeSort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <div className="flex items-center gap-1.5">
                    {col.header}
                    {col.sortable && activeSort?.key === col.key && (
                      <span className="text-trunnion-gold-500">
                        {activeSort.direction === 'asc' ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row) => (
              <tr
                key={keyExtractor(row)}
                className={`border-b border-trunnion-slate-700/50 transition-colors last:border-b-0 ${
                  onRowClick
                    ? 'cursor-pointer hover:bg-trunnion-slate-800/50'
                    : ''
                }`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3 text-trunnion-slate-200">
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between px-2 pt-4">
          <p className="text-sm text-trunnion-slate-400">
            Showing {(page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, total)} of {total} results
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
              icon={<ChevronLeft className="h-4 w-4" />}
            >
              Previous
            </Button>
            <span className="text-sm text-trunnion-slate-300">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages}
              icon={<ChevronRight className="h-4 w-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
